import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useCurrency } from '../context/CurrencyContext';  // 引入 Context
import './ShopInfoEdit.css';

function ProductForm({ merchantId }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const currencySymbol = useCurrency();  // 从 Context 获取货币符号

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [customCategoryId, setCustomCategoryId] = useState('');
  const [customCategories, setCustomCategories] = useState([]);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [available, setAvailable] = useState(true);
  const [soldOut, setSoldOut] = useState(false);

  // 获取自定义分类列表
  useEffect(() => {
    const tg = window.Telegram.WebApp;
    axios.get('/api/merchant/custom-categories', {
      headers: { 'x-telegram-init-data': tg.initData }
    })
      .then(res => setCustomCategories(res.data))
      .catch(err => console.error('获取自定义分类失败', err));
  }, []);

  // 编辑时获取商品详情
  useEffect(() => {
    if (id) {
      setLoading(true);
      const tg = window.Telegram.WebApp;
      axios.get(`/api/merchant/products/${id}`, {
        headers: { 'x-telegram-init-data': tg.initData }
      })
        .then(res => {
          const p = res.data;
          setName(p.name || '');
          setDescription(p.description || '');
          setPrice(p.price || '');
          setCustomCategoryId(p.custom_category_id || '');
          setImages(p.images || []);
          setAvailable(p.is_available !== undefined ? p.is_available : true);
          setSoldOut(p.is_sold_out === 1);
          setLoading(false);
        })
        .catch(() => {
          alert('加载商品失败');
          setLoading(false);
        });
    }
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'name') setName(value);
    else if (name === 'description') setDescription(value);
    else if (name === 'price') setPrice(value);
    else if (name === 'custom_category_id') setCustomCategoryId(value);
    else if (name === 'is_available') setAvailable(checked);
    else if (name === 'is_sold_out') setSoldOut(checked);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('请选择图片文件');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('图片大小不能超过 5MB');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('image', file);
    try {
      const res = await axios.post('/api/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImages(prev => [...prev, res.data.url]);
    } catch (err) {
      alert('上传失败：' + (err.response?.data?.error || err.message));
    } finally {
      setUploading(false);
    }
  };

  const handleAddUrl = () => {
    if (!newUrl.trim()) return;
    if (!newUrl.startsWith('http://') && !newUrl.startsWith('https://')) {
      alert('请输入有效的图片URL（以 http:// 或 https:// 开头）');
      return;
    }
    setImages(prev => [...prev, newUrl.trim()]);
    setNewUrl('');
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const payload = {
      name,
      description,
      price: parseFloat(price),
      custom_category_id: customCategoryId === '' ? null : customCategoryId,
      images,
      is_available: available,
      is_sold_out: soldOut ? 1 : 0
      // 不再需要传递 currency_code，后端会自动从商家信息获取
    };
    
    const tg = window.Telegram.WebApp;
    try {
      if (id) {
        await axios.put(`/api/merchant/products/${id}`, payload, {
          headers: { 'x-telegram-init-data': tg.initData }
        });
      } else {
        await axios.post('/api/merchant/products', payload, {
          headers: { 'x-telegram-init-data': tg.initData }
        });
      }
      navigate('/products');
    } catch (err) {
      alert('保存失败：' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="shop-info-edit-container" style={{ textAlign: 'center', padding: '40px' }}>加载中...</div>;

  return (
    <div className="shop-info-edit-container">
      <h2>{id ? '编辑商品' : '添加商品'}</h2>
      <form onSubmit={handleSubmit} className="shop-info-form">
        <div className="form-group">
          <label>名称</label>
          <input type="text" name="name" value={name} onChange={handleChange} required />
        </div>
        <div className="form-group">
          <label>描述</label>
          <textarea name="description" value={description} onChange={handleChange} rows="3" />
        </div>
        <div className="form-group">
          <label>价格 ({currencySymbol})</label>
          <input type="number" step="0.01" name="price" value={price} onChange={handleChange} required />
        </div>
        <div className="form-group">
          <label>自定义分类</label>
          <select name="custom_category_id" value={customCategoryId} onChange={handleChange}>
            <option value="">无分类</option>
            {customCategories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
          <small>选择后，顾客可按此分类筛选商品</small>
        </div>
        <div className="form-group">
          <label>商品图片（支持多张）</label>
          <div>
            <input type="file" accept="image/*" onChange={handleFileChange} disabled={uploading} />
            {uploading && <span> 上传中...</span>}
          </div>
          <div>
            <input
              type="text"
              value={newUrl}
              onChange={e => setNewUrl(e.target.value)}
              placeholder="或输入图片URL"
            />
            <button type="button" onClick={handleAddUrl}>添加URL</button>
          </div>
          {images.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '10px' }}>
              {images.map((url, idx) => (
                <div key={idx} style={{ position: 'relative', width: '100px' }}>
                  <img src={url} alt={`商品图${idx+1}`} style={{ width: '100%', height: '100px', objectFit: 'cover' }} />
                  <button
                    type="button"
                    onClick={() => removeImage(idx)}
                    style={{ position: 'absolute', top: '0', right: '0', background: 'red', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', cursor: 'pointer' }}
                  >×</button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="form-group">
          <label>
            <input type="checkbox" name="is_available" checked={available} onChange={handleChange} />
            上架
          </label>
        </div>
        <div className="form-group">
          <label>
            <input type="checkbox" name="is_sold_out" checked={soldOut} onChange={handleChange} />
            售罄（顾客可见但无法购买）
          </label>
        </div>
        <div className="form-group">
          <button type="submit" className="submit-btn" disabled={loading}>保存</button>
          <button type="button" className="upload-button" onClick={() => navigate('/products')} style={{ marginLeft: '10px' }}>取消</button>
        </div>
      </form>
    </div>
  );
}

export default ProductForm;